import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';

import type { WireIngestRow, WireRequeueResult } from '../types/ingest';

/**
 * Copilot's OWN record of what happened to each client file after the portal
 * accepted it: whether it reached the deal's Google Drive folder, and where.
 *
 * A separate slice from `documentInviteApi` on purpose. That one is a proxy to
 * the Odin Document Invite service and says so in its own header; these routes
 * are Copilot's, live under `/v1/document-ingest/*`, and are JWT-only with no
 * API-key path. Same document ids, different owner and different auth.
 *
 * ⚠️ These routes are mounted only when the Service Bus consumer that fills
 * the ledger is switched on — the module carrying the controller is imported
 * behind that flag. Wherever the consumer ships dark this 404s, which is a
 * state the screens must survive: see `utils/driveSync.ts`, where an absent
 * list reads as "unknown" rather than as "never announced".
 */
export const documentIngestApi = createApi({
  reducerPath: 'documentIngestApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    // Keyed by assessmentId. A requeue is the only write, and it can name the
    // document but not the assessment, so it invalidates the `LIST` sentinel.
    'IngestDocs',
  ],
  endpoints: (builder) => ({
    /**
     * Every document announced for one assessment, filed or not, in checklist
     * order.
     *
     * ⚠️ Not the same set as the request's documents. A document the backend
     * could not attribute to any assessment (`NO_LINK`) has no assessment to
     * be listed under and cannot appear here at all, and a document the portal
     * never announced has no row. Join on `documentId`; treat a miss as a
     * fact, not as a gap.
     */
    getIngestByAssessment: builder.query<WireIngestRow[], string>({
      query: (assessmentId) =>
        `v1/document-ingest/documents/by-assessment/${assessmentId}`,
      transformResponse: (r: ApiResponse<WireIngestRow[]>) => r.data,
      providesTags: (_result, _error, assessmentId) => [
        { type: 'IngestDocs', id: assessmentId },
        { type: 'IngestDocs', id: 'LIST' },
      ],
    }),

    /**
     * Drives a parked document's filing job again: the row goes back to
     * RECEIVED with its reason cleared, and the job re-runs from hydration -
     * creating the Drive folder if it is still missing.
     *
     * ⚠️ `requeued: false` is a success, not a refusal: the document was
     * already FILED and nothing was queued. A document with no ledger row
     * 404s. Safe to repeat - the filing job adopts its own earlier upload
     * rather than duplicating it.
     */
    requeueIngestDocument: builder.mutation<WireRequeueResult, string>({
      query: (documentId) => ({
        url: `v1/document-ingest/documents/${documentId}/requeue`,
        method: 'POST',
      }),
      transformResponse: (r: ApiResponse<WireRequeueResult>) => r.data,
      invalidatesTags: [{ type: 'IngestDocs', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetIngestByAssessmentQuery,
  useRequeueIngestDocumentMutation,
} = documentIngestApi;
